import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MoreVertical, Flag, UserX, ShieldAlert } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useProfile } from "@/context/ProfileContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { createReportApi, blockUserApi } from "@/lib/api";

const REASONS = ["spam", "harassment", "inappropriate", "other"];
const NOTE_MAX = 500;

/**
 * Phase 10 (Google Play UGC compliance): overflow menu on a feed card with
 * Report post / Report user / Block user actions. Report and Block flows are
 * intentionally separate (user decision).
 */
export const FeedCardMenu = ({ post, onBlocked }) => {
  const { profile } = useProfile();
  const { t } = useLanguage();

  // report dialog state: null | "post" | "profile"
  const [reportTarget, setReportTarget] = useState(null);
  const [reason, setReason] = useState("spam");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  // block confirmation state
  const [blockOpen, setBlockOpen] = useState(false);
  const [blocking, setBlocking] = useState(false);

  const openReport = (targetType) => {
    setReason("spam");
    setNote("");
    setReportTarget(targetType);
  };

  const submitReport = async () => {
    setSending(true);
    try {
      await createReportApi({
        reporter_id: profile.id,
        target_type: reportTarget,
        target_id: reportTarget === "post" ? post.id : post.profile_id,
        reason,
        note: note.trim() || null,
      });
      toast.success(t("report.success"));
      setReportTarget(null);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.info(t("report.duplicate"));
        setReportTarget(null);
      } else {
        toast.error(t("report.error"));
      }
    } finally {
      setSending(false);
    }
  };

  const confirmBlock = async () => {
    setBlocking(true);
    try {
      await blockUserApi(profile.id, post.profile_id);
      toast.success(t("block.blockedToast", { name: post.author_name }));
      setBlockOpen(false);
      onBlocked?.(post.profile_id);
    } catch {
      toast.error(t("block.error"));
    } finally {
      setBlocking(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`feed-card-menu-${post.id}`}
            aria-label={t("report.menuAria")}
            className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          >
            <MoreVertical size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="bg-slate-900/95 backdrop-blur-xl border-white/10 text-white"
        >
          <DropdownMenuItem
            data-testid={`feed-card-report-post-${post.id}`}
            onClick={() => openReport("post")}
            className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer"
          >
            <Flag size={14} className="text-cyan-300/80" />
            {t("report.reportPost")}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`feed-card-report-user-${post.id}`}
            onClick={() => openReport("profile")}
            className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer"
          >
            <ShieldAlert size={14} className="text-cyan-300/80" />
            {t("report.reportUser")}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`feed-card-block-user-${post.id}`}
            onClick={() => setBlockOpen(true)}
            className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer text-red-300 focus:text-red-300"
          >
            <UserX size={14} />
            {t("block.blockUser")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* ---- Report dialog ---- */}
      <Dialog open={reportTarget !== null} onOpenChange={(o) => !o && setReportTarget(null)}>
        <DialogContent
          data-testid="report-dialog"
          className="max-w-md bg-slate-900/95 backdrop-blur-xl border-white/10 text-white"
        >
          <DialogHeader>
            <DialogTitle>
              {reportTarget === "profile" ? t("report.titleUser") : t("report.titlePost")}
            </DialogTitle>
            <DialogDescription className="text-[hsl(var(--muted-foreground))]">
              {t("report.desc")}
            </DialogDescription>
          </DialogHeader>

          <RadioGroup value={reason} onValueChange={setReason} className="gap-2.5">
            {REASONS.map((r) => (
              <div key={r} className="flex items-center gap-2.5">
                <RadioGroupItem
                  value={r}
                  id={`report-reason-${r}`}
                  data-testid={`report-reason-${r}`}
                  className="border-white/30 text-cyan-300"
                />
                <Label htmlFor={`report-reason-${r}`} className="text-sm text-white/85 cursor-pointer">
                  {t(`report.reason.${r}`)}
                </Label>
              </div>
            ))}
          </RadioGroup>

          <div>
            <Label htmlFor="report-note" className="text-xs text-[hsl(var(--muted-foreground))]">
              {t("report.noteLabel")}
            </Label>
            <Textarea
              id="report-note"
              data-testid="report-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("report.notePlaceholder")}
              maxLength={NOTE_MAX}
              rows={3}
              className="mt-1.5 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/35 focus-visible:ring-2 focus-visible:ring-cyan-300/60 resize-none"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              data-testid="report-cancel-button"
              variant="outline"
              onClick={() => setReportTarget(null)}
              className="rounded-xl bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white"
            >
              {t("report.cancel")}
            </Button>
            <Button
              data-testid="report-submit-button"
              onClick={submitReport}
              disabled={sending}
              className="rounded-xl bg-cyan-300 text-slate-950 font-semibold hover:bg-cyan-200 active:scale-[0.98] disabled:opacity-60"
            >
              {sending ? <Loader2 className="animate-spin" size={16} /> : t("report.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Block confirmation ---- */}
      <AlertDialog open={blockOpen} onOpenChange={setBlockOpen}>
        <AlertDialogContent
          data-testid="block-confirm-dialog"
          className="max-w-md bg-slate-900/95 backdrop-blur-xl border-white/10 text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("block.title", { name: post.author_name })}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[hsl(var(--muted-foreground))]">
              {t("block.desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="block-cancel-button"
              className="rounded-xl bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white"
            >
              {t("block.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="block-confirm-button"
              onClick={(e) => {
                e.preventDefault();
                confirmBlock();
              }}
              disabled={blocking}
              className="rounded-xl bg-red-400/90 text-slate-950 font-semibold hover:bg-red-300 active:scale-[0.98] disabled:opacity-60"
            >
              {blocking ? <Loader2 className="animate-spin" size={16} /> : t("block.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
